import 'react-day-picker/dist/style.css'; // Fix issue: Global CSS cannot be imported from files other than your Custom
import 'react-lite-youtube-embed/dist/LiteYouTubeEmbed.css';

import { ChakraProvider } from '@chakra-ui/react';
import { theme } from '@diamond/shared/theme';
import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import React from 'react';
import * as ReactDOM from 'react-dom/client';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import App from './app';

Sentry.init({
  dsn: process.env['NX_SENTRY_DSN_ADMIN'],
  environment: process.env['NX_ENV'],
  attachStacktrace: true,
  autoSessionTracking: true,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration(),
  ],

  tracesSampleRate: 1.0,

  tracePropagationTargets: [
    /^https:\/\/api-v2\.sukandaonelink\.com\/api/,
    'localhost',
  ],

  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,

  // Static error messages to ignore - RFC compliant filtering
  ignoreErrors: [
    // Business rule errors
    'Minimum order not met',
    'MIN_ORDER_NOT_REACHED',
    'MIN_ORDER_NOT_REACHED_DUE_TO_TOTAL_DIFF',
    'ALL_ITEM_CANT_BE_PROCESSED',

    // Authorization and token errors
    /Authorization token expired/i,
    'JWT_AUTHORIZATION_TOKEN_EXPIRED',
    /Unauthorized/i,
    'Request failed with status code 401',
    'Request failed with status code 403',

    // Network and timeout errors
    /Network Error/i,
    'Failed to fetch',
    'Timeout of 10000ms exceeded',
    /timeout/i,
    'ERR_NETWORK',
    'ECONNABORTED',

    // Admin-specific UI errors
    'Cancel rendering route',
    /cancel rendering/i,
    'Failed to connect to MetaMask',
    /metamask/i,

    // Verification and validation messages
    'Mohon menunggu saat ini data Anda dalam proses verifikasi',
    /verifikasi.*data/i,
    /awaiting verification/i,

    // Common browser errors
    'ResizeObserver loop limit exceeded',
    'Non-Error promise rejection captured',
    /ChunkLoadError/i,
    /Loading chunk/i,

    // Admin-specific errors
    'Permission denied',
    /permission.*denied/i,
    'Access forbidden',
    /access.*forbidden/i,
  ],

  beforeSend(event, hint) {
    const msg =
      (hint.originalException as Error)?.message ||
      (event as any).message ||
      '';
    const errorValue = event.exception?.values?.[0]?.value || '';

    // Pattern-based filtering for dynamic messages
    const patterns = [
      /minimum order/i,
      /token expired/i,
      /verifikasi.*data/i,
      /cancel rendering/i,

      /failed to fetch/i,
      /network error/i,
      /timeout/i,
      /unauthorized/i,
      /permission.*denied/i,
      /access.*forbidden/i,
      /metamask/i,
      /chunk.*error/i,
      /loading.*chunk/i,
    ];

    // Check if message matches any ignore pattern
    if (
      patterns.some((pattern) => pattern.test(msg) || pattern.test(errorValue))
    ) {
      return null;
    }

    // Filter out handled exceptions marked as such
    if (errorValue.includes('handled') || msg.includes('handled')) {
      return null;
    }

    // Downgrade minor errors to warnings
    if (
      event.level === 'error' &&
      (errorValue.includes('minor') || msg.includes('minor'))
    ) {
      event.level = 'warning';
    }

    // Downgrade business logic errors to warnings
    const businessErrors = [
      'MIN_ORDER_NOT_REACHED',
      'ALL_ITEM_CANT_BE_PROCESSED',
      'DIFF_PRICE_OR_STOCK',
      'PARTIALLY_OUT_OF_STOCK',
      'PERMISSION_DENIED',
      'ACCESS_FORBIDDEN',
    ];

    if (
      event.level === 'error' &&
      businessErrors.some(
        (err) => errorValue.includes(err) || msg.includes(err)
      )
    ) {
      event.level = 'warning';
    }

    // Add context for better debugging
    if (event.tags) {
      event.tags['filtered_by'] = 'sol-admin';
      event.tags['app'] = 'admin';
    }

    return event;
  },
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <StrictMode>
    <ChakraProvider theme={theme}>
      <App />
    </ChakraProvider>
  </StrictMode>
);
